import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Bullets, Inputs, Spinner, FlexContainer, ModalContext } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { userStageSelector } from 'Store/modules/validations/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import useValidateClabe, { getErrorContext, hasError } from 'Hooks/useValidateClabe';
import SuccessPurchase from 'Containers/Filing/components/ClabeForm/components/SuccessPurchase';
import { useFetchFilingUser, usePatchFilingUser } from 'Containers/Filing/hooks';
import { TaxReturnTypes } from 'Containers/Result/utils';
import AutoRenewalModal from 'Containers/Result/components/AutoRenewalModal';
import CustomTransition from 'Components/CustomTransition';
import OneColLayout from 'Components/Layouts/OneColLayout';
import { openLinkInNewTab } from 'Utils/links';
import { CLABE_HELP_URL } from 'Constants/helpsUrl';
import { iconsPaths } from 'Constants/iconsPath';
import { routes } from 'Constants/routes';
import { stages } from 'Constants/stages';
import * as Styled from './styles';

const ClabeInputFlex = (): JSX.Element => {
  const { showModal } = useContext(ModalContext);
  const [clabeAccount, setClabeAccount] = useState('');
  const { isValid, error, bankTag, isLoading: isLoadingValidate } = useValidateClabe(clabeAccount);
  const { t } = useTranslation(locizeNS.FILING);
  const infoSubscription = useSelector(routeStateSelector);
  const userStage = useSelector(userStageSelector);
  const { data, isLoading } = useFetchFilingUser();
  const { mutate, isSuccess, isLoading: loading } = usePatchFilingUser();

  useEffect(() => {
    showModal({
      comp: <AutoRenewalModal />,
    });
  }, []);

  useEffect(() => {
    if (userStage && userStage !== stages.SUBMITTED) {
      history.push(routes.DASHBOARD);
    }
  }, [userStage]);

  useEffect(() => {
    if (data?.CLABE) {
      setClabeAccount(data?.CLABE);
    }
  }, [data]);

  useEffect(() => {
    if (isSuccess) {
      history.push(routes.FILING_FLEX, {
        taxReturnType: TaxReturnTypes.TO_RETURN,
      });
    }
  }, [isSuccess]);

  const handleChange = (val): void => {
    setClabeAccount(val.replace(/\s+/g, ''));
  };
  const handleClickSaveCard = (): void => {
    mutate({ CLABE: clabeAccount });
  };

  return (
    <OneColLayout>
      <CustomTransition start>
        <Styled.Container>
          {isLoading || loading ? (
            <Spinner size="M" align="center" justify="center" />
          ) : (
            <FlexContainer direction="column" align="center" gap="dimension_16" justify="center">
              {infoSubscription?.plan ? (
                <SuccessPurchase plan={infoSubscription.plan.name.toString()} />
              ) : (
                <img src={iconsPaths.TAX} alt="taxito" />
              )}
              <Inputs.Text
                label={t('ClabeForm.Clabe.label')}
                description={t(`ClabeForm.toReturn.description`, {
                  context: data?.CLABE ? 'review' : '',
                })}
                value={clabeAccount}
                hasError={!isLoadingValidate && hasError(clabeAccount, isValid)}
                onChange={handleChange}
                placeholder={t('Filing.clabe.placeholder')}
                errorMsg={t(`Filing.clabe.${getErrorContext(clabeAccount, error)}`)}
                helpButtonUrl={CLABE_HELP_URL}
                onHelpButton={url => openLinkInNewTab(url)}
              />
              {clabeAccount && isValid && (
                <Styled.BulletsContainer>
                  <Bullets
                    bullets={[
                      {
                        text: t('Filing.clabe.tag', { info: bankTag }),
                      },
                    ]}
                    icon={{
                      type: 'IconInfoLineSDark',
                    }}
                  />
                </Styled.BulletsContainer>
              )}
              {infoSubscription?.isInstantRefund && (
                <Styled.ChipBanner size="L" type="success_shaded" mt="fds_size_08">
                  {t('InstantRefund.flex.info')}
                </Styled.ChipBanner>
              )}
              <Styled.ButtonsContainer>
                <Styled.ButtonsActions
                  disabled={!isValid || isLoadingValidate}
                  testID="payLater"
                  size="m"
                  onClick={() => handleClickSaveCard()}
                  label={t('Result.CTA.flex')}
                />
              </Styled.ButtonsContainer>
            </FlexContainer>
          )}
        </Styled.Container>
      </CustomTransition>
    </OneColLayout>
  );
};

export default ClabeInputFlex;
